import React from 'react';
import axios from "axios";

import { useMantineTheme, Box, Group, Flex, ActionIcon, Card, SimpleGrid, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowsSort } from '@tabler/icons-react';

import { useLoginContext } from "../../context/loginContext";

import { BE_API_ONE, utils } from "../../utils/globalVariables";
import { regexNoStriped } from "../../utils/globalFunctions";

import AmountOfData from "../../components/general/AmountOfData";
import ModalWrapperWithoutFooter from "../../components/general/ModalWrapperWithoutFooter";
import LoaderLine from "../../components/general/LoaderLine";
import SortMenu from "../../components/general/SortMenu";
import NoData from "../../components/general/NoData";
import SearchInputComponent from "../../components/general/SearchInputComponent";

const StockCategorySelectorDrawer = ({
    stateShow,
    fnShow,
    fnOnSelect,
}) => {

    const mountedRef = React.useRef(true);

    const theme = useMantineTheme();
    const { loginContext, handleSetLoginContext } = useLoginContext();

    const notificationId = React.useRef({
        errorGetData: 'stockCategorySelectorDrawer-1',
    })

    const filterDefaultRef = React.useRef({
        stockCategoryIsActive: utils.flagAll,
    })

    const sortList = {
        stockCategoryName: {
            slug: 'stock_category_name',
            caption: 'Nama',
            type: utils.sortType.string.slug
        },
        stockCategoryIsActive: {
            slug: 'stock_category_is_active',
            caption: 'Status',
            type: utils.sortType.integer.slug
        },
    }

    const [data, setData] = React.useState({
        data: [],
        dataCount: 0,
        dataFlag: 'idle',
        dataLoading: false,
        dataRefresh: new Date(),
        dataOffset: 0,
        dataSortSelected: sortList.stockCategoryName,
        dataSearchInputForBE: '',

        filterData: {
            stockCategoryIsActive: filterDefaultRef.current.stockCategoryIsActive,
        },
    });

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    const fnSort = (stateSortSelected) => {
        setData(d => ({
            ...d,
            dataLoading: true,
            dataSortSelected: {
                ...d.dataSortSelected,
                slug: d.dataSortSelected.slug[0] === '-' ? stateSortSelected.slug : '-' + stateSortSelected.slug,
                caption: stateSortSelected.caption,
                type: stateSortSelected.type
            }
        }));
    };

    const AmountOfDataComponent = () => {
        return (
            <AmountOfData
                stateDataLimit={utils.dataLimit}
                stateDataOffset={data.dataOffset}
                stateAmountDataAll={data.dataCount}
                stateDataFlag={data.dataFlag}
                stateSuffix=''

                fnFirstData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnLastData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnPreviousData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnNextData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}
            />
        )
    }

    React.useEffect(() => {
        setData(d => ({ ...d, dataLoading: true }));

        const formDT = new FormData();
        const formDTHeader = { 'Content-Type': 'application/json' }

        formDT.append('json_token', loginContext.syswebapp);
        formDT.append('data_limit', utils.dataLimit);
        formDT.append('data_offset', data.dataOffset);
        formDT.append('order_by', regexNoStriped(data.dataSortSelected.slug));
        formDT.append('order_type', data.dataSortSelected.slug[0] === '-' ? 'asc' : 'desc');

        formDT.append('flag_all', utils.flagAll);
        formDT.append('filter_search', data.dataSearchInputForBE);
        formDT.append('filter_stock_category_is_active', data.filterData.stockCategoryIsActive);

        axios({
            method: 'post',
            url: BE_API_ONE + '/get_stock_category',
            data: formDT,
            headers: formDTHeader,
        }).then((response) => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, dataLoading: false }));

            if (response.data.flag_status === 'isset') {
                setData(d => ({
                    ...d,
                    data: response.data.data,
                    dataCount: response.data.data_count,
                    dataFlag: response.data.data.length > 0 ? true : false,
                }));
            }

            if (response.data.flag_status === 'invalid-json-token') {
                handleSetLoginContext(null);
            }

            if (response.data.flag_status === 'user-inactive') {
                handleSetLoginContext(null);
            }
        }).catch(error => {
            if (!mountedRef.current) return null;

            setData(d => ({
                ...d,
                dataLoading: false,
            }));

            notifications.show({
                id: notificationId.current.errorGetData,
                title: utils.notification.warning.caption,
                message: error.message,
                color: utils.notification.warning.color,
                icon: utils.notification.warning.icon,
                withBorder: true,
                autoClose: true,
            })
        })
    }, [handleSetLoginContext, loginContext.syswebapp, data.dataRefresh, data.dataSearchInputForBE, data.dataSortSelected.slug, data.dataOffset, data.filterData.stockCategoryIsActive])

    return (
        <ModalWrapperWithoutFooter
            stateShow={stateShow}
            fnHide={fnShow}
            stateHideOnClickOutSide={true}
            stateTitle={String('Daftar Stok Kategori').toUpperCase()}
            stateSize='md'
            stateOverlayZIndex={201}
            stateWithCloseButton={true}
        >
            <Box
                sx={{
                    position: 'relative'
                }}
            >
                <Group
                    noWrap
                    mb='sm'
                    pb="sm"

                    sx={{
                        borderBottom: `1px solid ${theme.colorScheme === 'light' ? theme.colors.gray[3] : theme.colors.dark[5]}`
                    }}
                >
                    <SortMenu
                        stateSortList={sortList}
                        stateSortSelected={data.dataSortSelected}
                        stateComponentTarget={<ActionIcon
                            size='lg'
                            variant="default"
                            radius='xl'
                        >
                            <IconArrowsSort size='1.25rem' stroke={1} />
                        </ActionIcon>}
                        fnSort={fnSort}
                    />

                    <SearchInputComponent
                        statePlaceHolder='Search Stok Kategori'
                        fnDoSearch={(val) => setData(d => ({
                            ...d,
                            dataOffset: 0,
                            dataSearchInputForBE: val,
                            dataRefresh: new Date(),
                        }))}
                    />
                </Group>

                {data.dataLoading &&
                    <LoaderLine
                        statePosition='absolute'
                        stateIsTop={false}
                    />
                }
            </Box>

            {data.data.length > 0 &&
                <SimpleGrid
                    cols={1}
                    spacing='xs'
                >
                    {Object.keys(data.data).map((key, index) => {
                        return (
                            <Card
                                key={key}
                                shadow=''
                                radius="md"
                                withBorder={true}

                                sx={(tema) => ({
                                    borderColor: tema.colorScheme === 'light' ? tema.colors.gray[2] : tema.colors.dark[7],
                                    backgroundColor: tema.colorScheme === 'light' ? tema.colors.gray[0] : tema.colors.dark[6],
                                    overflow: 'unset',
                                    cursor: 'pointer',
                                    transition: '200ms',
                                    '&:hover': {
                                        borderColor: tema.colorScheme === 'light' ? tema.colors.blue[2] : tema.fn.rgba(tema.colors.blue[6], 0.5),
                                        backgroundColor: tema.colorScheme === 'light' ? tema.colors.blue[0] : tema.fn.rgba(tema.colors.blue[6], 0.1),
                                    }
                                })}

                                onClick={() => fnOnSelect(
                                    data.data[key].stock_category_id,
                                    data.data[key].stock_category_name,
                                    data.data[key].stock_category_is_active
                                )}
                            >
                                <Card.Section p='sm'>
                                    <Text size="sm" fw={500}>
                                        {data.data[key].stock_category_name}
                                    </Text>

                                    <Text size="xs">
                                        {data.data[key].stock_category_is_active === '1' ? 'Aktif' : 'Non Aktif'}
                                    </Text>
                                </Card.Section>
                            </Card>
                        )
                    })}
                </SimpleGrid>
            }

            {data.data.length <= 0 &&
                <NoData />
            }

            <Flex
                justify="flex-end"
                align="center"
                direction="row"
                wrap='nowrap'
                mt='lg'
            >
                <AmountOfDataComponent />
            </Flex>
        </ModalWrapperWithoutFooter>
    )
}

export default StockCategorySelectorDrawer;